import { Box } from 'portal-commons';
import { ChangeEvent, FunctionComponent } from 'react';
import { useController, useForm } from 'react-hook-form';
import {
  faAward,
  faShareNodes,
  faThumbsUp,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid, FormControlLabel } from '@material-ui/core';

import { CarrierSelectionFormValues, MnoMetadata } from '../../types';
import { S3_ASSETS_PATH } from '@/constants';
import { CustomTooltip } from '@/shared_elements';
import { CheckBox } from '@/ui_elements';

interface CarrierSelectionFormProps {
  mnos: MnoMetadata[];
  defaultValues?: CarrierSelectionFormValues;
  onPrevious?: () => void;
  onNext?: (values: CarrierSelectionFormValues) => void;
}

const CarrierSelectionForm: FunctionComponent<CarrierSelectionFormProps> = ({
  mnos,
  defaultValues,
  onPrevious,
  onNext,
}) => {
  const {
    control,
    formState: { isSubmitting, errors },
    handleSubmit,
  } = useForm<CarrierSelectionFormValues>({
    defaultValues: {
      mnoIds: [],
      ...(defaultValues ?? {}),
    },
  });
  const {
    field: { value: mnoIds, onChange: setMnoIds },
  } = useController({
    name: 'mnoIds',
    control,
  });

  const handleCheckBoxClick = (
    event: ChangeEvent<HTMLInputElement>,
    mnoId: string
  ) => {
    const { checked } = event.currentTarget;
    if (mnoId !== undefined) {
      const newMnoIds = checked
        ? [...mnoIds, mnoId]
        : mnoIds.filter((id) => id !== mnoId);
      setMnoIds(newMnoIds);
    }
  };

  const handleValid = (values: CarrierSelectionFormValues) => {
    onNext?.(values);
  };

  return (
    <form noValidate onSubmit={handleSubmit(handleValid)}>
      <Grid container spacing={0} alignItems="center">
        <Grid item xs={12} data-testid="mnoPreviewNew">
          <div className="mno-preview wrapper mno-select-wrapper">
            <div className="heading-block">
              <Box flexDirection="row" alignItems="center">
                <Box margin={{ right: 'xs' }}>
                  <FontAwesomeIcon icon={faShareNodes} size="xl" />
                </Box>
                <h3 className="heading1">Carrier Terms Preview</h3>
              </Box>
            </div>
            <div className="mno-overview">
              <p className="paragraph title">
                The below list shows campaign qualification results and terms
                for each MNO.
              </p>
              <ul className="mno-listing list-unstyled">
                {mnos.map((mnoObject, index) => (
                  <li key={index}>
                    <FormControlLabel
                      className="mno-select"
                      label=""
                      labelPlacement="end"
                      disabled={!mnoObject.qualify}
                      control={
                        <CheckBox
                          type="mno-preview"
                          id={mnoObject.mnoMetadataKey}
                          disabled={false}
                          negative={false}
                          checked={mnoIds.includes(mnoObject.mnoMetadataKey)}
                          handleCheckbox={(e: ChangeEvent<HTMLInputElement>) =>
                            handleCheckBoxClick(e, mnoObject.mnoMetadataKey)
                          }
                        />
                      }
                    />
                    <Grid container>
                      <Grid item xs={2} className="name">
                        {mnoObject.mno}
                      </Grid>
                      <Grid item xs={10}>
                        <Grid container>
                          <Grid
                            item
                            xs={2}
                            className="text-center"
                            data-testid="mnoPreviewNewQualify"
                          >
                            <h3 className="paragraph bold">
                              <Box
                                flexDirection="row"
                                justifyContent="center"
                                alignItems="center"
                                padding={{ top: 'xs' }}
                              >
                                <Box margin={{ right: 'xxs' }}>
                                  <FontAwesomeIcon icon={faAward} size="xl" />
                                </Box>
                                <span>QUALIFY</span>
                              </Box>
                            </h3>
                            <p className="paragraph light">
                              {mnoObject.qualify ? (
                                'Yes'
                              ) : (
                                <img
                                  style={{
                                    position: 'relative',
                                    bottom: '5px',
                                  }}
                                  src={`${S3_ASSETS_PATH}/images/not-qualify.svg`}
                                />
                              )}
                            </p>
                          </Grid>
                          <Grid
                            item
                            xs={3}
                            data-testid="mnoPreviewNewMnoReview"
                          >
                            <h3 className="paragraph bold">
                              <Box
                                flexDirection="row"
                                justifyContent="center"
                                alignItems="center"
                                padding={{ top: 'xs' }}
                              >
                                <Box margin={{ right: 'xxs' }}>
                                  <FontAwesomeIcon
                                    icon={faThumbsUp}
                                    size="xl"
                                  />
                                </Box>
                                <span>MNO REVIEW</span>
                              </Box>
                            </h3>
                            <p className="paragraph light">
                              {mnoObject.mnoReview ? 'Yes' : 'No'}
                            </p>
                          </Grid>
                          {mnoObject.mno === 'T-Mobile' ? (
                            <>
                              <Grid
                                item
                                xs={2}
                                style={{ maxWidth: '17%' }}
                                data-testid="mnoPreviewNewBrandTier"
                              >
                                <h3
                                  className="paragraph bold tooltip"
                                  style={{ marginLeft: 5, paddingTop: 5 }}
                                >
                                  BRAND
                                  <br />
                                  TIER
                                </h3>
                                {
                                  <CustomTooltip
                                    arrow
                                    title={`“Please check the Knowledge Base for the ”Daily and Monthly maximum throughput values per Brand” associated with this tier.`}
                                    placement="top"
                                  >
                                    <p
                                      className="paragraph light"
                                      style={{ marginLeft: 5 }}
                                    >
                                      {mnoObject.brandTier
                                        ? mnoObject.brandTier
                                        : 'N/A'}
                                    </p>
                                  </CustomTooltip>
                                }
                              </Grid>
                              <Grid
                                item
                                xs={2}
                                style={{ maxWidth: '14%' }}
                                data-testid="mnoPreviewNewBrandDailyCap"
                              >
                                <h3
                                  className="paragraph bold tooltip"
                                  style={{ paddingTop: 5 }}
                                >
                                  BRAND
                                  <br />
                                  DAILY CAP
                                </h3>
                                {mnoObject.mno === 'T-Mobile' ? (
                                  <p className="paragraph light">
                                    {mnoObject.brandDailyCap
                                      ? mnoObject.brandDailyCap
                                      : mnoObject &&
                                        mnoObject.brandTier &&
                                        mnoObject.brandTier === 'UNCAPPED'
                                      ? 'No'
                                      : 'N/A'}
                                  </p>
                                ) : (
                                  <p className="paragraph light">
                                    {mnoObject.tpmScope
                                      ? mnoObject.tpmScope
                                      : 'N/A'}
                                  </p>
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={2}
                                style={{ maxWidth: '10%' }}
                                data-testid="mnoPreviewNewMnsTpm"
                              >
                                <h3
                                  className="paragraph bold tooltip"
                                  style={{ paddingTop: 5 }}
                                >
                                  MMS
                                  <br />
                                  TPM
                                </h3>
                                {mnoObject.mno === 'T-Mobile' &&
                                mnoObject.mmsTpm ? (
                                  <CustomTooltip
                                    arrow
                                    title={`“Please check the Knowledge Base for the ”Daily and Monthly maximum throughput values per Brand” associated with this tier.`}
                                    placement="top"
                                  >
                                    <p className="paragraph light">
                                      {mnoObject.mmsTpm ?? 'N/A'}
                                    </p>
                                  </CustomTooltip>
                                ) : (
                                  <p className="paragraph light">
                                    {mnoObject.mmsTpm ?? 'N/A'}
                                  </p>
                                )}
                              </Grid>
                            </>
                          ) : (
                            <>
                              <Grid
                                item
                                xs={2}
                                style={{ maxWidth: '17%' }}
                                data-testid="mnoPreviewNewTpmScope"
                              >
                                <h3
                                  className="paragraph bold tooltip"
                                  style={{ marginLeft: 5, paddingTop: 5 }}
                                >
                                  TPM
                                  <br />
                                  SCOPE
                                </h3>
                                {mnoObject.mno === 'T-Mobile' &&
                                mnoObject.brandDailyCap ? (
                                  <p
                                    className="paragraph light"
                                    style={{ marginLeft: 5 }}
                                  >
                                    {mnoObject.brandDailyCap}
                                  </p>
                                ) : (
                                  <p
                                    className="paragraph light"
                                    style={{
                                      marginLeft: 5,
                                      paddingRight: 8,
                                      paddingLeft: 8,
                                      wordBreak: 'keep-all',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                    title={mnoObject.tpmScope || 'N/A'}
                                  >
                                    {mnoObject.tpmScope
                                      ? mnoObject.tpmScope
                                      : 'N/A'}
                                  </p>
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={2}
                                style={{ maxWidth: '14%' }}
                                data-testid="mnoPreviewNewSmsTpm"
                              >
                                <h3
                                  className="paragraph bold tooltip"
                                  style={{ paddingTop: 5 }}
                                >
                                  SMS
                                  <br />
                                  TPM
                                </h3>
                                {mnoObject.mno === 'AT&T' ? (
                                  <p className="paragraph light">
                                    {mnoObject.tpm ? mnoObject.tpm : 'N/A'}
                                  </p>
                                ) : (
                                  <p className="paragraph light">
                                    {mnoObject.tpm ? mnoObject.tpm : 'N/A'}
                                  </p>
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={2}
                                style={{ maxWidth: '10%' }}
                                data-testid="mnoPreviewNewMmsTpm"
                              >
                                <h3
                                  className="paragraph bold tooltip"
                                  style={{ paddingTop: 5 }}
                                >
                                  MMS
                                  <br />
                                  TPM
                                </h3>
                                {
                                  <p className="paragraph light">
                                    {mnoObject.mmsTpm ?? 'N/A'}
                                  </p>
                                }
                              </Grid>
                            </>
                          )}
                          <Grid
                            item
                            xs={2}
                            data-testid="mnoPreviewNewMessageClass"
                          >
                            <h3
                              className="paragraph bold"
                              style={{ paddingTop: 5 }}
                            >
                              MESSAGE
                              <br />
                              CLASS
                            </h3>
                            <p className="paragraph light">
                              {mnoObject.msgClass ?? 'N/A'}
                            </p>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </li>
                ))}
              </ul>
              <br />
              {!!errors.mnoIds?.message && (
                <p className="error-msg-brand-relationship">
                  {errors.mnoIds.message}
                </p>
              )}
            </div>
          </div>
        </Grid>
      </Grid>
      <Grid item xs={12} className="bottom-blk">
        <ul className="list-inline">
          <li className="">
            <p>
              <a
                data-testid="campaignRegisterFormPreviousButton"
                className="secondary-btn"
                onClick={onPrevious}
              >
                Previous
              </a>
            </p>
          </li>
          <li className="form-group-field">
            <input
              data-testid="campaignRegisterFormNextButton"
              type="submit"
              disabled={
                isSubmitting ||
                mnoIds.length <= 0 ||
                Object.values(errors).some((error) => !!error?.message)
              }
              value={isSubmitting ? 'Processing..' : 'Next'}
            />
          </li>
        </ul>
      </Grid>
    </form>
  );
};

export default CarrierSelectionForm;
